import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useInfiniteScroll from '../../common/hooks/useInfiniteScroll';
import { parseString } from '../utils/stringHelper';

const iList = [];
const iQuery = {
  searchTerm: '',
  page: 0,
  size: 16,
  sort: ['createdOn,desc'],
  archived: false,
  hasNextPage: true,
  folder: null,
  omitFiledStories: false,
  approvalStatus: null,
  tags: null,
};

export default function useFetchListWithInfiniteScroll(options) {
  const { initList = iList, initQuery = iQuery, service, bottomMinOffset, reset, scrollContainer, filterOutIds = [] } = options;

  // TODO: There's a possibility that list is undefined
  const [list, setList] = useState(initList);
  const [query, setQuery] = useState(initQuery);
  const [loading, setLoading] = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const [isEmptyList, setEmptyList] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [initListFlag, toggleInitListFlag] = useState(true);
  const isSearching = useMemo(() => query.searchTerm !== initQuery.searchTerm || query?.tags !== null, [
    initQuery.searchTerm,
    query.searchTerm,
    query.tags,
  ]);
  const onSearch = useCallback((type, value) => setQuery(prevState => ({ ...prevState, [type]: value, page: 1, hasNextPage: true })), [
    query,
  ]);
  const onApprovalStatusFilter = useCallback(value => setQuery({ ...query, approvalStatus: value }), [query]);

  const cancelProbing = useRef(false);

  const listFilteredById = useMemo(() => filterOutIds?.length ? list.filter(item => !filterOutIds?.find(id => item.id == id)) : list, [list, filterOutIds])

  useEffect(() => {
    setEmptyList(false);
  }, [service]);

  // didMount & subsequent didUpdate with comparing previous state of query.searchterm against the current one.
  useEffect(() => {
    console.log(' // didMount & subsequent didUpdate');
    let didCancel = false;

    const loadList = async () => {
      if (!didCancel) setLoading(true);

      if (cancelProbing.current) {
        return;
      }

      let probeList = [];
      let probedRaw = localStorage.getItem('probed');
      let alreadyProbed = probedRaw !== null ? JSON.parse(probedRaw) : {};
      let probeAgain = false;

      try {
        const content = await service({
          ...initQuery,
          searchTerm: query.searchTerm,
          tags: query?.tags ? query?.tags?.join(',') : null,
          approvalStatus: query.approvalStatus,
        });
        if (initListFlag) {
          setQuery(prevQuery => {
            const nextPageExists = content.pagination?.total <= initQuery.size ? false : true; // If we receive less than a full size response, there is no next page
            if (nextPageExists) {
              return { ...prevQuery, page: initQuery.page + 1, hasNextPage: true };
            } else {
              return { ...prevQuery, hasNextPage: false };
            }
          });
          toggleInitListFlag(false);
        }

        if (!didCancel) {
          setTotalData(content.pagination?.total);
          setList(content.result.map(item => ({ ...item, name: parseString(item.name) })));
        }

        // Check if any of the returned videos have no thumbnail, implying they have not finished rendering
        content.result.forEach(element => {
          if (!element.draft && element.thumbnail === null && element.source == 'web') {
            probeList.push(element.id);

            alreadyProbed[element.id] = element.id in alreadyProbed ? alreadyProbed[element.id] + 1 : 1;

            // Check localStorage if we've already probed for all these videos more than 6 times (= 30 secs @ 5 sec intervals)
            if (alreadyProbed[element.id] < 6) {
              probeAgain = true;
            }
          }
        });

        if (alreadyProbed !== null && Object.keys(alreadyProbed).length > 0) {
          // Store already probed status in localStorage
          localStorage.setItem('probed', JSON.stringify(alreadyProbed));
        }

        console.log('loadList', content.result.length, query.searchTerm);
        if (content.result.length === 0 && query.searchTerm === '' && query.tags.length === 0) setEmptyList(true);

        // Update the video list again in 5 seconds
        if (probeAgain) {
          console.log('Probing again in 5 seconds');
          setTimeout(loadList, 5000);
        }
      } catch (e) {
        console.log(e.message);
      } finally {
        if (!didCancel) setLoading(false);
      }
    };
    if (service) {
      setLoading(true);
      loadList();
    } else {
      setLoading(false);
    }
    return () => {
      didCancel = true;
      cancelProbing.current = false;
    };
  }, [initQuery, query.searchTerm, query?.tags, query.approvalStatus, service]);

  useEffect(() => {
    console.log('initQuery', initQuery);
  }, [initQuery]);

  // trigger by useInfiniteScroll whenever query.hasNextPage set to true and it reach certain scroll point;
  const loadMore = useCallback(async () => {
    cancelProbing.current = true;
    console.log('load more triggered', query.hasNextPage, 'query', query);
    // list fetching goes here whenever query state is set
    if (!query.hasNextPage) return;
    setLoading(true);
    setMoreLoading(true);
    try {
      const content = await service({
        ...query,
        tags: query?.tags ? query?.tags?.join(',') : null,
      });

      setTotalData(content.pagination?.total);

      setQuery(prevQuery => {
        // If we receive less than a full size response, there is no next page
        let nextPageExists = content.result.length + list.length < totalData ? true : false;
        if (content.result.length === 0) {
          nextPageExists = false;
        }
        console.log('nextPageExists', nextPageExists);
        if (nextPageExists) {
          return {
            ...prevQuery,
            page: content.result.length === 0 ? prevQuery.page : prevQuery.page + 1,
            hasNextPage: true,
          };
        } else {
          return { ...prevQuery, hasNextPage: false };
        }
      });
      setList(prevState => [...prevState, ...content.result.map(item => ({ ...item, name: parseString(item.name) }))]);
      if (content.result.length === 0 && list.length === 0 && query.searchTerm === '' && query.tags.length === 0) setEmptyList(true);
    } catch (e) {
      console.log(e.message);
    } finally {
      setLoading(false);
      setMoreLoading(false);
    }
  }, [query, service, list]);

  const searchInput = useMemo(() => ({ value: query.searchTerm, onChange: onSearch }), [onSearch, query.searchTerm]);

  const loadList = useCallback(async () => {
    if (isEmptyList) setEmptyList(false);
    setLoading(true);
    try {
      const content = await service({
        ...initQuery,
        searchTerm: query.searchTerm,
        approvalStatus: query.approvalStatus,
      });
      setTotalData(content.pagination?.total);
      setList(content.result.map(item => ({ ...item, name: parseString(item.name) })));
      setQuery(prevQuery => {
        const nextPageExists = content.result.length + list.length < totalData ? true : false;
        if (nextPageExists) {
          return {
            ...prevQuery,
            page: content.result.length === 0 ? prevQuery.page : prevQuery.page + 1,
            hasNextPage: true,
          };
        } else {
          return { ...prevQuery, hasNextPage: false };
        }
      });

      if (content.result.length === 0 && query.searchTerm === '' && query.tags.length === 0) {
        setEmptyList(true);
      }
    } catch (e) {
      console.log(e.message);
    } finally {
      setLoading(false);
    }
  }, [initQuery, isEmptyList, query.searchTerm, query.approvalStatus, service]);

  const loadAllList = useCallback(async () => {
    console.log('load all list triggered', query.hasNextPage, 'query', query);
    if (isEmptyList) setEmptyList(false);
    setLoading(true);
    const lastPage = query.page;
    const queriesList = [];
    for (let i = 0; i <= lastPage; i++) {
      queriesList.push({ ...query, page: i });
    }

    try {
      const data = await Promise.all(
        queriesList.map(q => {
          return service(q);
        })
      );
      setTotalData(data[0].pagination?.total);
      const arrayRes = [];
      data.forEach(item => arrayRes.push(...item.result.map(res => ({ ...res, name: parseString(res.name) }))));
      setList(arrayRes);
      if (arrayRes.length === 0 && query.searchTerm === '' && query.tags.length === 0) {
        setEmptyList(true);
      }
    } catch (e) {
      console.log('Error while Promise.all: ', e);
    } finally {
      setLoading(false);
    }
  }, [query, service]);

  useEffect(() => {
    reset && toggleInitListFlag(true);
  }, [reset]);

  const infiniteRef = useInfiniteScroll({
    hasNextPage: query.hasNextPage,
    loading,
    onLoadMore: loadMore,
    threshold: 150,
    bottomMinOffset,
    scrollContainer, 
  });

  return {
    infiniteRef,
    list,
    listFilteredById,
    setList,
    totalData,
    loadList,
    isEmptyList,
    setEmptyList,
    isSearching,
    loading,
    moreLoading,
    searchInput,
    query,
    setTotalData,
    loadAllList,
    onApprovalStatusFilter,
  };
}
